import { useEffect, useCallback } from 'react';
import { Canvas as FabricCanvas, Rect, Textbox, FabricImage, Line as FabricLine, FabricObject, Path as FabricPath, FabricText, Group as FabricGroup, ActiveSelection, loadSVGFromString, util as fabricUtil } from 'fabric';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { useMappingStore } from '../stores/mappingStore';
import { loadCustomFont, isFontLoaded, isFontReady } from '../lib/fontLoader';
import { applyVisualsToFabricObject } from '../lib/fabricVisuals';
import { getDashArray } from '../lib/elementDefaults';
import { createFabricObjectFromElement, resolveTextPathRef, setCrossOriginOnImages } from '../lib/fabricFactory';
import { updateFabricObjectFromElement, applySvgFillResolution } from '../lib/fabricSync';
import { auditTextWidths, ELEMENT_ID_KEY, ARTBOARD_PADDING } from '../lib/textMeasurement';
import type { CanvasElement, TextElement, ShapeElement, ImageElement, ButtonElement, GroupElement, LineElement, PathElement, SvgElement } from '../types';

export function useCanvasSync(opts: {
  fabricCanvas: FabricCanvas | null;
  artboardRef: React.RefObject<Rect | null>;
}) {
  const {
    showBackgroundLayer,
    showOverlayLayer,
  } = useCanvasStore();

  const { canvasState } = useStudioStore();

  // syncElementsToCanvas useCallback (lines 1491-1787)
  const syncElementsToCanvas = useCallback(() => {
    if (!opts.fabricCanvas || !opts.artboardRef.current) return;

    // Children of an ActiveSelection use group-local left/top; discard before world-coord writes.
    let multiSelectIdsToRestore: string[] | null = null;
    // Single active objects are kept, not discarded, so a remove-and-recreate pass can re-select the fresh object.
    let singleSelectIdToRestore: string | null = null;
    const activeBeforeSync = opts.fabricCanvas.getActiveObject();
    if (activeBeforeSync instanceof ActiveSelection) {
      multiSelectIdsToRestore = activeBeforeSync
        .getObjects()
        .map((o) => (o as any)[ELEMENT_ID_KEY] as string)
        .filter(Boolean);
      opts.fabricCanvas.discardActiveObject();
    } else if (activeBeforeSync) {
      singleSelectIdToRestore = ((activeBeforeSync as any)[ELEMENT_ID_KEY] as string) ?? null;
    }

    const artboard = opts.artboardRef.current;
    const artboardLeft = artboard.left ?? ARTBOARD_PADDING;
    const artboardTop = artboard.top ?? ARTBOARD_PADDING;

    const freshElements = useStudioStore.getState().canvasState.elements;

    const existingObjects = opts.fabricCanvas.getObjects().filter(
      (obj) => !(obj as any)._csIsArtboard && !(obj as any)._csIsGrid && !(obj as any)._csIsPreview
        && !(obj as any)._csPenPreview && !(obj as any)._csPenDot && !(obj as any)._csIsArtboardBorder
    );

    const existingMap = new Map<string, FabricObject>();
    existingObjects.forEach((obj) => {
      const id = (obj as any)[ELEMENT_ID_KEY] as string;
      if (!id) return;
      const prev = existingMap.get(id);
      if (prev && (obj as any)._csIsSvg && !(prev as any)._csIsSvg) {
        existingMap.set(id, obj);
      } else if (!prev) {
        existingMap.set(id, obj);
      }
    });

    const groupChildIds = new Set(
      freshElements
        .filter((el) => el.type === 'group')
        .flatMap((el) => (el as GroupElement).childIds)
    );

    const topLevelElements = freshElements;
    const currentIds = new Set(topLevelElements.map((el) => el.id));

    existingObjects.forEach((obj) => {
      const id = (obj as any)[ELEMENT_ID_KEY] as string;
      if (id && !currentIds.has(id)) {
        opts.fabricCanvas.remove(obj);
      } else if (id && currentIds.has(id) && !(obj as any)._csIsSvg) {
        const preferred = existingMap.get(id);
        if (preferred && preferred !== obj && (preferred as any)._csIsSvg) {
          opts.fabricCanvas.remove(obj);
        }
      }
    });

    topLevelElements.forEach((element) => {
      const existing = existingMap.get(element.id);

      const isVisible =
        element.visible &&
        ((element.layer === 'BACKGROUND_LAYER' && showBackgroundLayer) ||
         (element.layer === 'OVERLAY_LAYER' && showOverlayLayer));

      if (existing) {
        if (element.type === 'path') {
          const pathEl = element as PathElement;
          opts.fabricCanvas.remove(existing);
          const newObj = createFabricObjectFromElement(element, artboardLeft, artboardTop);
          if (newObj) {
            (newObj as any)[ELEMENT_ID_KEY] = element.id;
            applyVisualsToFabricObject(newObj, element);
            newObj.set({
              visible: isVisible,
              selectable: !element.locked && isVisible,
              evented: !element.locked && isVisible,
            });
            opts.fabricCanvas.add(newObj);
          }
        } else if (element.type === 'text') {
          const textEl = element as TextElement;
          const resolvedPathData = resolveTextPathRef(textEl, freshElements) || textEl.pathData;
          const needsPath = !!resolvedPathData;
          const isTextbox = existing instanceof Textbox;
          const isFabricTextOnly = existing instanceof FabricText && !isTextbox;

          if ((needsPath && isTextbox) || (!needsPath && isFabricTextOnly)) {
            opts.fabricCanvas.remove(existing);
            const newObj = createFabricObjectFromElement(element, artboardLeft, artboardTop);
            if (newObj) {
              (newObj as any)[ELEMENT_ID_KEY] = element.id;
              applyVisualsToFabricObject(newObj, element);
              newObj.set({
                visible: isVisible,
                selectable: !element.locked && isVisible,
                evented: !element.locked && isVisible,
              });
              opts.fabricCanvas.add(newObj);
            }
          } else if (needsPath && isFabricTextOnly) {
            existing.set({
              left: artboardLeft + element.x,
              top: artboardTop + element.y,
              angle: element.rotation,
              opacity: element.opacity,
              visible: isVisible,
              selectable: !element.locked && isVisible,
              evented: !element.locked && isVisible,
            });
            updateFabricObjectFromElement(existing, element);
            applyVisualsToFabricObject(existing, element);
            existing.setCoords();
          } else {
            existing.set({
              left: artboardLeft + element.x,
              top: artboardTop + element.y,
              width: element.width,
              scaleX: 1,
              scaleY: 1,
              angle: element.rotation,
              opacity: element.opacity,
              visible: isVisible,
              selectable: !element.locked && isVisible,
              evented: !element.locked && isVisible,
            });
            updateFabricObjectFromElement(existing, element);
            applyVisualsToFabricObject(existing, element);
            existing.setCoords();
          }
        } else if (element.type === 'line' && existing instanceof FabricLine) {
          const lineEl = element as LineElement;
          existing.set({
            x1: artboardLeft + lineEl.x + lineEl.x1,
            y1: artboardTop + lineEl.y + lineEl.y1,
            x2: artboardLeft + lineEl.x + lineEl.x2,
            y2: artboardTop + lineEl.y + lineEl.y2,
            angle: 0,
            scaleX: 1,
            scaleY: 1,
            stroke: lineEl.stroke,
            strokeWidth: lineEl.strokeWidth,
            strokeDashArray: getDashArray(lineEl.strokeDashStyle, lineEl.strokeWidth),
            strokeLineCap: lineEl.lineCap,
            opacity: lineEl.opacity,
            visible: isVisible,
            selectable: !lineEl.locked && isVisible,
            evented: !lineEl.locked && isVisible,
          });
          existing.setCoords();
        } else if (element.type === 'svg') {
        const svgEl = element as SvgElement;
        let svgTarget = existing;
        if (!(existing as any)._csIsSvg) {
          const realGroup = opts.fabricCanvas.getObjects().find(
            (o) => (o as any)[ELEMENT_ID_KEY] === element.id && (o as any)._csIsSvg
          );
          if (realGroup) {
            svgTarget = realGroup;
          } else {
            svgTarget.set({ visible: isVisible });
            svgTarget.setCoords();
          }
        }
        if ((svgTarget as any)._csIsSvg) {
          const cachedMarkup = (svgTarget as any)._csSvgMarkup;
          if (cachedMarkup && cachedMarkup !== svgEl.svgMarkup) {
            opts.fabricCanvas.remove(svgTarget);
            if (svgTarget !== existing) opts.fabricCanvas.remove(existing);
            loadSVGFromString(svgEl.svgMarkup).then(({ objects, options }) => {
              if (!opts.fabricCanvas) return;
              const validObjs = objects.filter((o): o is FabricObject => o !== null);
              if (validObjs.length === 0) return;
              const grp = fabricUtil.groupSVGElements(validObjs, options);
              setCrossOriginOnImages(grp);
              grp.set({
                left: artboardLeft + svgEl.x,
                top: artboardTop + svgEl.y,
                scaleX: svgEl.width / (svgEl.originalWidth || 1),
                scaleY: svgEl.height / (svgEl.originalHeight || 1),
                angle: svgEl.rotation || 0,
                opacity: svgEl.opacity ?? 1,
                visible: isVisible,
                selectable: !svgEl.locked && isVisible,
                evented: !svgEl.locked && isVisible,
                lockUniScaling: svgEl.preserveAspectRatio,
              });
              (grp as any)[ELEMENT_ID_KEY] = svgEl.id;
              (grp as any)._csIsSvg = true;
              (grp as any)._csSvgMarkup = svgEl.svgMarkup;
              opts.fabricCanvas.add(grp);
              applySvgFillResolution(grp, svgEl);
              opts.fabricCanvas.requestRenderAll();
            }).catch(() => {});
          } else {
            const svgScaleX = svgEl.width / (svgEl.originalWidth || 1);
            const svgScaleY = svgEl.height / (svgEl.originalHeight || 1);
            console.log('[Sync] SVG dimension sync', {
              id: svgEl.id,
              storeW: svgEl.width,
              storeH: svgEl.height,
              originalW: svgEl.originalWidth,
              originalH: svgEl.originalHeight,
              scaleX: svgScaleX,
              scaleY: svgScaleY,
            });
            svgTarget.set({
              left: artboardLeft + svgEl.x,
              top: artboardTop + svgEl.y,
              scaleX: svgScaleX,
              scaleY: svgScaleY,
              angle: svgEl.rotation || 0,
              opacity: svgEl.opacity ?? 1,
              visible: isVisible,
              selectable: !svgEl.locked && isVisible,
              evented: !svgEl.locked && isVisible,
              lockUniScaling: svgEl.preserveAspectRatio,
            });
            svgTarget.setCoords();
            applySvgFillResolution(svgTarget, svgEl);
          }
        }
        } else {
        const isImg = existing instanceof FabricImage;
        const isText = existing instanceof Textbox;
        const isButton = (existing as any)._csIsButton === true;

        let dimensionProps: Record<string, number> = {};

        if (isImg) {
          const intrinsicW = (existing as FabricImage).width ?? element.width;
          const intrinsicH = (existing as FabricImage).height ?? element.height;
          const targetScaleX = intrinsicW > 0 ? element.width / intrinsicW : 1;
          const targetScaleY = intrinsicH > 0 ? element.height / intrinsicH : 1;
          dimensionProps = { scaleX: targetScaleX, scaleY: targetScaleY };
          console.log('[Sync] Image dimension sync', {
            id: element.id,
            storeW: element.width,
            storeH: element.height,
            intrinsicW,
            intrinsicH,
            scaleX: targetScaleX,
            scaleY: targetScaleY,
          });
        } else if (isText) {
          dimensionProps = { width: element.width, scaleX: 1, scaleY: 1 };
        } else {
          dimensionProps = { width: element.width, height: element.height, scaleX: 1, scaleY: 1 };
        }

        // Phase F.2 / Decision [22] / Decision [12]: Coordinate convention.
        // Store convention: element.x and element.y represent the visual TOP-LEFT
        // of the element, regardless of type. This is canonical across all paths
        // (migration, create, modify, sync).
        //
        // Fabric convention:
        //   - Buttons (FabricGroup) use originX/originY: 'center', so Fabric's
        //     `left` and `top` represent the visual CENTER.
        //   - All other Fabric objects use the default origin (top-left), so
        //     Fabric's `left` and `top` represent the visual TOP-LEFT.
        //
        // When writing from store to Fabric for a button, we must translate
        // top-left → center by adding width/2 and height/2.
        const writeLeft = isButton
          ? artboardLeft + element.x + (element.width / 2)
          : artboardLeft + element.x;
        const writeTop = isButton
          ? artboardTop + element.y + (element.height / 2)
          : artboardTop + element.y;

        // DIAGNOSTIC — temporary
        if (element.type === 'group') {
          console.log('[GROUP-DEBUG-SYNC]', {
            stage: 'syncElementsToCanvas-group-write',
            elementId: element.id,
            storeCoords: { x: element.x, y: element.y, width: element.width, height: element.height },
            fabricArtboardOffset: { artboardLeft, artboardTop },
            computedFabricWrite: { writeLeft, writeTop },
            isButton,
            dimensionProps,
          });
        }
        // END DIAGNOSTIC

        existing.set({
          left: writeLeft,
          top: writeTop,
          ...dimensionProps,
          angle: element.rotation,
          opacity: element.opacity,
          visible: isVisible,
          selectable: !element.locked && isVisible,
          evented: !element.locked && isVisible,
        });
        updateFabricObjectFromElement(existing, element);
        applyVisualsToFabricObject(existing, element);
        existing.setCoords();
        }
      } else {
        const staleObjs = opts.fabricCanvas.getObjects().filter(
          (o) => (o as any)[ELEMENT_ID_KEY] === element.id
        );
        for (const stale of staleObjs) {
          opts.fabricCanvas.remove(stale);
        }
        const fabricObj = createFabricObjectFromElement(element, artboardLeft, artboardTop);
        if (fabricObj) {
          (fabricObj as any)[ELEMENT_ID_KEY] = element.id;
          applyVisualsToFabricObject(fabricObj, element);
          fabricObj.set({
            visible: isVisible,
            selectable: !element.locked && isVisible,
            evented: !element.locked && isVisible,
          });
          opts.fabricCanvas.add(fabricObj);
        }
      }
    });

    const sortedElements = [...topLevelElements].sort((a, b) => a.zIndex - b.zIndex);
    sortedElements.forEach((element, index) => {
      const obj = opts.fabricCanvas.getObjects().find(
        (o) => (o as any)[ELEMENT_ID_KEY] === element.id
      );
      if (obj) {
        opts.fabricCanvas.moveObjectTo(obj, index + 1);
      }
    });

    opts.fabricCanvas.getObjects().forEach((obj) => {
      const id = (obj as any)[ELEMENT_ID_KEY] as string | undefined;
      if (id && groupChildIds.has(id)) {
        obj.set({ selectable: false, evented: false });
        (obj as any).hoverCursor = 'default';
      }
      if ((obj as any)._csIsArtboardBorder) opts.fabricCanvas.bringObjectToFront(obj);
    });

    // Rebuild the multi-select captured above; removed elements drop out of the rebuilt selection.
    if (multiSelectIdsToRestore && multiSelectIdsToRestore.length > 0) {
      const objectsToReselect = multiSelectIdsToRestore
        .map((id) => opts.fabricCanvas.getObjects().find((o) => (o as any)[ELEMENT_ID_KEY] === id))
        .filter(Boolean) as FabricObject[];
      if (objectsToReselect.length > 1) {
        opts.fabricCanvas.setActiveObject(new ActiveSelection(objectsToReselect, { canvas: opts.fabricCanvas }));
      } else if (objectsToReselect.length === 1) {
        opts.fabricCanvas.setActiveObject(objectsToReselect[0]);
      }
    }

    if (singleSelectIdToRestore) {
      const activeAfterSync = opts.fabricCanvas.getActiveObject();
      const activeStillOnCanvas =
        !!activeAfterSync && opts.fabricCanvas.getObjects().includes(activeAfterSync);
      if (!activeStillOnCanvas) {
        const fresh = opts.fabricCanvas
          .getObjects()
          .find((o) => (o as any)[ELEMENT_ID_KEY] === singleSelectIdToRestore);
        if (fresh) {
          opts.fabricCanvas.setActiveObject(fresh);
        }
      }
    }

    opts.fabricCanvas.requestRenderAll();
    requestAnimationFrame(() => {
      opts.fabricCanvas.renderAll();
      auditTextWidths(opts.fabricCanvas);
    });
  }, [opts.fabricCanvas, showBackgroundLayer, showOverlayLayer]);

  // Font loading + sync trigger effect (lines 1789-1804)
  useEffect(() => {
    const fontsNeeded = canvasState.elements
      .filter((el): el is TextElement | ButtonElement => el.type === 'text' || el.type === 'button')
      .map((el) => el.fontFamily)
      .filter((f, i, arr) => f && !isFontLoaded(f) && arr.indexOf(f) === i);

    if (fontsNeeded.length > 0) {
      let cancelled = false;
      Promise.all(fontsNeeded.map((f) => loadCustomFont(f))).then(() => {
        if (!cancelled) syncElementsToCanvas();
      });
      return () => { cancelled = true; };
    }

    syncElementsToCanvas();
  }, [syncElementsToCanvas, canvasState.elements]);

  // Font-settle audit (lines 1806-1819)
  useEffect(() => {
    if (!opts.fabricCanvas) return;
    const fontset = (document as unknown as { fonts?: FontFaceSet }).fonts;
    if (!fontset || typeof fontset.addEventListener !== 'function') return;
    const onLoadingDone = () => {
      requestAnimationFrame(() => auditTextWidths(opts.fabricCanvas));
    };
    fontset.addEventListener('loadingdone', onLoadingDone);
    return () => fontset.removeEventListener('loadingdone', onLoadingDone);
  }, [opts.fabricCanvas]);

  // editAsBrand re-sync (lines 1821-1829)
  const editAsBrandId = useStudioStore(s => s.editAsBrandId);
  const editAsBrandData = useStudioStore(s => s.editAsBrandData);

  useEffect(() => {
    if (opts.fabricCanvas) {
      syncElementsToCanvas();
    }
  }, [editAsBrandId, editAsBrandData, syncElementsToCanvas]);

  // elementMappings re-sync (lines 1831-1835)
  const elementMappings = useMappingStore(s => s.elementMappings);

  useEffect(() => {
    if (opts.fabricCanvas) {
      syncElementsToCanvas();
    }
  }, [elementMappings, syncElementsToCanvas, opts.fabricCanvas]);

  // Selection sync (lines 2606-2658)
  useEffect(() => {
    if (!opts.fabricCanvas) return;
    const unsub = useStudioStore.subscribe((state) => {
      const selectedIds = state.selectedElementIds || [];
      const currentActive = opts.fabricCanvas.getActiveObject();

      const currentActiveIds = new Set<string>();
      if (currentActive) {
        if (currentActive instanceof ActiveSelection) {
          currentActive.getObjects().forEach((obj: FabricObject) => {
            const id = (obj as any)[ELEMENT_ID_KEY];
            if (id) currentActiveIds.add(id);
          });
        } else {
          const id = (currentActive as any)[ELEMENT_ID_KEY];
          if (id) currentActiveIds.add(id);
        }
      }

      if (selectedIds.length === currentActiveIds.size &&
          selectedIds.every(id => currentActiveIds.has(id))) {
        return;
      }

      if (selectedIds.length === 0) {
        opts.fabricCanvas.discardActiveObject();
        opts.fabricCanvas.requestRenderAll();
      } else if (selectedIds.length === 1) {
        const obj = opts.fabricCanvas.getObjects().find(
          (o) => (o as any)[ELEMENT_ID_KEY] === selectedIds[0]
        );
        if (obj) {
          opts.fabricCanvas.setActiveObject(obj);
          opts.fabricCanvas.requestRenderAll();
        }
      } else {
        const objects = selectedIds
          .map(id => opts.fabricCanvas.getObjects().find((o) => (o as any)[ELEMENT_ID_KEY] === id))
          .filter(Boolean) as FabricObject[];

        if (objects.length > 1) {
          opts.fabricCanvas.discardActiveObject();
          const activeSelection = new ActiveSelection(objects, { canvas: opts.fabricCanvas });
          opts.fabricCanvas.setActiveObject(activeSelection);
          opts.fabricCanvas.requestRenderAll();
        } else if (objects.length === 1) {
          opts.fabricCanvas.setActiveObject(objects[0]);
          opts.fabricCanvas.requestRenderAll();
        }
      }
    });
    return unsub;
  }, [opts.fabricCanvas]);
}
